import { useState } from "react";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import EmberButton from "@/components/EmberButton";
import { submitInquiry } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { MapPin, Phone, Mail, Clock, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

const EVENT_TYPES = [
    { value: "wedding", label: "Wedding" },
    { value: "corporate", label: "Corporate" },
    { value: "private", label: "Private Party" },
    { value: "birthday", label: "Birthday" },
    { value: "graduation", label: "Graduation" },
    { value: "festival", label: "Festival / Pop-up" },
    { value: "other", label: "Other" },
];
const BUDGETS = [
    { value: "<$500", label: "Under $500" },
    { value: "$500-$1000", label: "$500 – $1,000" },
    { value: "$1000-$2500", label: "$1,000 – $2,500" },
    { value: "$2500-$5000", label: "$2,500 – $5,000" },
    { value: "$5000+", label: "$5,000+" },
    { value: "flexible", label: "Flexible / Not sure" },
];

export default function ContactPage() {
    const { settings } = useSettings();
    const s = settings || {};
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        event_type: "",
        event_date: "",
        guest_count: "",
        location: "",
        menu_preferences: "",
        budget: "",
        notes: "",
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.name || !form.email || !form.event_type) {
            toast.error("Please fill in your name, email and event type.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                guest_count: form.guest_count ? Number(form.guest_count) : 0,
            };
            await submitInquiry(payload);
            setSubmitted(true);
            toast.success("Inquiry sent! Mo will be in touch soon.");
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="section">
                <div className="container-narrow">
                    <SectionHeader
                        eyebrow="Book Mo"
                        title="Tell us about your event."
                        subtitle="Fill out a quick inquiry and we'll get back fast with availability and a tailored menu."
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-7">
                            {submitted ? (
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                                    <Card className="surface-card border-0" data-testid="booking-success-card">
                                        <CardContent className="p-8 text-center">
                                            <CheckCircle2 size={42} className="text-orange-300 mx-auto" />
                                            <div className="font-display text-2xl mt-4 text-white">Inquiry Sent</div>
                                            <p className="text-white/75 mt-2">Thanks, {form.name.split(" ")[0] || "friend"}. Mo will reach out shortly.</p>
                                            <button
                                                className="mt-5 ghost-ember px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase"
                                                onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", event_type: "", event_date: "", guest_count: "", location: "", menu_preferences: "", budget: "", notes: "" }); }}
                                                data-testid="booking-submit-another-button"
                                            >Submit another</button>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ) : (
                            <Card className="surface-card border-0" data-testid="booking-form-card">
                                <CardContent className="p-6 sm:p-8">
                                    <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={submit} data-testid="booking-form">
                                        <div className="sm:col-span-2">
                                            <Label className="text-white/80" htmlFor="name">Your Name *</Label>
                                            <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="First and last name" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" data-testid="booking-name-input" />
                                        </div>
                                        <div>
                                            <Label className="text-white/80" htmlFor="email">Email *</Label>
                                            <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" data-testid="booking-email-input" />
                                        </div>
                                        <div>
                                            <Label className="text-white/80" htmlFor="phone">Phone</Label>
                                            <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 555-5555" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" data-testid="booking-phone-input" />
                                        </div>
                                        <div>
                                            <Label className="text-white/80">Event Type *</Label>
                                            <Select value={form.event_type} onValueChange={(v) => update("event_type", v)}>
                                                <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="booking-event-type-select">
                                                    <SelectValue placeholder="Choose one" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {EVENT_TYPES.map((o) => (
                                                        <SelectItem key={o.value} value={o.value} data-testid={`booking-event-type-option-${o.value}`}>{o.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-white/80" htmlFor="event_date">Event Date</Label>
                                            <Input id="event_date" type="date" value={form.event_date} onChange={(e) => update("event_date", e.target.value)} className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50" data-testid="booking-event-date-input" />
                                        </div>
                                        <div>
                                            <Label className="text-white/80" htmlFor="guest_count">Guest Count</Label>
                                            <Input id="guest_count" type="number" min="0" value={form.guest_count} onChange={(e) => update("guest_count", e.target.value)} placeholder="e.g. 50" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" data-testid="booking-guest-count-input" />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-white/80" htmlFor="location">Event Location</Label>
                                            <Input id="location" value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Address or venue" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" data-testid="booking-location-input" />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-white/80" htmlFor="menu_preferences">Menu Preferences</Label>
                                            <Textarea id="menu_preferences" rows={4} value={form.menu_preferences} onChange={(e) => update("menu_preferences", e.target.value)} placeholder="Brisket-heavy? Vegetarian options? Tell Mo what you're thinking." className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" data-testid="booking-menu-preferences-textarea" />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-white/80">Budget</Label>
                                            <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                                                <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="booking-budget-select">
                                                    <SelectValue placeholder="Pick a range" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {BUDGETS.map((o) => (
                                                        <SelectItem key={o.value} value={o.value} data-testid={`booking-budget-option-${o.value.replace(/[^a-z0-9]+/gi,'-')}`}>{o.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-white/80" htmlFor="notes">Notes / Anything Else</Label>
                                            <Textarea id="notes" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Setup constraints, allergies, special requests..." className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" data-testid="booking-notes-textarea" />
                                        </div>
                                        <div className="sm:col-span-2 flex items-center justify-end">
                                            <EmberButton asChild>
                                                <button disabled={submitting} type="submit" data-testid="booking-submit-button">
                                                    {submitting ? "Sending..." : "Send Inquiry"}
                                                </button>
                                            </EmberButton>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                            )}
                        </div>
                        <div className="lg:col-span-5 space-y-4">
                            <Card className="surface-card border-0" data-testid="contact-info-card">
                                <CardContent className="p-6">
                                    <div className="font-display text-xl tracking-[0.05em] mb-4">Reach Mo Directly</div>
                                    <ul className="text-white/85 space-y-3">
                                        <li className="flex items-start gap-2"><MapPin size={16} className="mt-1 text-orange-300" /> <span data-testid="contact-address">{s.address || "15 Route 206 S, Sandyston, NJ 07826"}</span></li>
                                        <li className="flex items-start gap-2"><Phone size={16} className="mt-1 text-orange-300" /> <a className="hover:text-orange-300" href={`tel:${(s.phone || '8623032001').replace(/\D/g,'')}`} data-testid="contact-phone-link">{s.phone || "862-303-2001"}</a></li>
                                        <li className="flex items-start gap-2"><Mail size={16} className="mt-1 text-orange-300" /> <a className="hover:text-orange-300" href={`mailto:${s.email || 'smokinmosnj@gmail.com'}`} data-testid="contact-email-link">{s.email || "smokinmosnj@gmail.com"}</a></li>
                                        <li className="flex items-start gap-2"><Clock size={16} className="mt-1 text-orange-300" /> <span data-testid="contact-hours">{s.hours || "Thursday – Sunday · 11am – 7pm"}</span></li>
                                    </ul>
                                </CardContent>
                            </Card>
                            <Card className="surface-card border-0" data-testid="contact-map-card">
                                <CardContent className="p-0 overflow-hidden rounded-xl">
                                    <iframe
                                        title="Smokin Mo's location"
                                        src={`https://www.google.com/maps?q=${encodeURIComponent(s.address || '15 Route 206 S, Sandyston, NJ 07826')}&output=embed`}
                                        width="100%"
                                        height="260"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
